import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { formatPrice } from '../mock';
import { fetchProductById, fetchProducts, resolveImageUrl, getProductCardImages } from '../lib/api';
import { useCart } from '../context/CartContext';
import { Minus, Plus, Truck, ShieldCheck, RefreshCcw, Gem, ChevronLeft, ChevronRight, ClipboardList, Gift } from 'lucide-react';
import { toast } from 'sonner';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import ProductCard from '../components/ProductCard';

const PROMISE_ITEMS = [
  { icon: Gem, label: '24K Gold Vermeil' },
  { icon: ShieldCheck, label: 'IGI Certified Diamonds' },
  { icon: Truck, label: 'Free Insured Shipping' },
  { icon: RefreshCcw, label: 'Lifetime Exchange' },
  { icon: RefreshCcw, label: '7-Day Return' }
];

const ORDERS_SHIPMENTS_ITEMS = [
  {
    icon: ClipboardList,
    q: 'How safe is the shipping of my Jewelry?',
    bullets: [
      ['Your Jewelry is shipped in ', { b: 'secure, tamper-proof packaging.' }, ' We only work with trusted courier partners, and each shipment is ', { b: 'fully insured' }, ' until it reaches you.']
    ]
  },
  {
    icon: Gift,
    q: 'What is the expected delivery time?',
    bullets: [
      ['We deliver all orders within ', { b: '3 weeks' }, ' of confirmation.'],
      ['Once your order is ready a Client advisor will get in touch to co-ordinate delivery in-store or shipment.']
    ]
  },
  {
    icon: Truck,
    q: 'Which locations do you deliver to?',
    bullets: [
      ['We offer complementary shipping to all ', { b: 'major cities in India,' }, ' usually within 2 working days.'],
      ['For ', { b: 'remote locations' }, ' please check with us the possibility, additional charges may apply.'],
      [{ b: 'International Shipping' }, ' is available at additional charges based on country and applicable import duties/tariff. Please get in touch to know more.']
    ]
  }
];

const RETURNS_POLICY_CARDS = [
  {
    title: 'Return & Exchange Policy',
    body: 'You can return or exchange your House of Seya jewelry within 7 days of delivery. Items must be unused, undamaged, and returned in their original packaging with all accompanying documentation, including the IGI certificate.'
  },
  {
    title: 'Lifetime Buyback Policy',
    body: 'House of Seya offers a Lifetime Buyback Program on eligible pieces purchased directly from our website, letting you exchange jewelry you no longer wear for store credit toward a future purchase, for as long as you own the piece.'
  },
  {
    title: 'Additional Return & Exchange Terms',
    bullets: ['Returned items must be unused, undamaged, and accompanied by original packaging and documentation.'],
    body: 'For full terms, refer to our complete Returns, Exchange & Buyback Policy page.'
  }
];

const WhatsAppIcon = ({ size = 20, color = '#3fc352' }) => (
  <svg viewBox="0 0 448 512" width={size} height={size} fill={color} aria-hidden="true">
    <path d="M380.9 97.1C339 55.1 283.2 32 223.9 32c-122.4 0-222 99.6-222 222 0 39.1 10.2 77.3 29.6 111L0 480l117.7-30.9c32.4 17.7 68.9 27 106.1 27h.1c122.3 0 224.1-99.6 224.1-222 0-59.3-25.2-115-67.1-157zm-157 341.6c-33.2 0-65.7-8.9-94-25.7l-6.7-4-69.8 18.3L72 359.2l-4.4-7c-18.5-29.4-28.2-63.3-28.2-98.2 0-101.7 82.8-184.5 184.6-184.5 49.3 0 95.6 19.2 130.4 54.1 34.8 34.9 56.2 81.2 56.1 130.5 0 101.8-84.9 184.6-186.6 184.6zm101.2-138.2c-5.5-2.8-32.8-16.2-37.9-18-5.1-1.9-8.8-2.8-12.5 2.8-3.7 5.6-14.3 18-17.6 21.8-3.2 3.7-6.5 4.2-12 1.4-32.6-16.3-54-29.1-75.5-66-5.7-9.8 5.7-9.1 16.3-30.3 1.8-3.7.9-6.9-.5-9.7-1.4-2.8-12.5-30.1-17.1-41.2-4.5-10.8-9.1-9.3-12.5-9.5-3.2-.2-6.9-.2-10.6-.2-3.7 0-9.7 1.4-14.8 6.9-5.1 5.6-19.4 19-19.4 46.3 0 27.3 19.9 53.7 22.6 57.4 2.8 3.7 39.1 59.7 94.8 83.8 35.2 15.2 49 16.5 66.6 13.9 10.7-1.6 32.8-13.4 37.4-26.4 4.6-13 4.6-24.1 3.2-26.4-1.3-2.5-5-3.9-10.5-6.6z" />
  </svg>
);

const PRODUCT_FAQ_GROUPS = [
  {
    group: 'Certification & Authenticity',
    items: [
      { q: 'Is jewelry certification provided?', a: 'Yes. Every House of Seya diamond, regardless of carat size, ships with an IGI certificate — not just diamonds above a certain weight.' },
      { q: 'Is the silver hallmarked?', a: "Yes. Every piece is crafted on a BIS hallmarked sterling silver base, certified to the purity standard set by India's Bureau of Indian Standards." },
      { q: 'How can I verify if my product is authentic?', a: "You can check the IGI Diamond Certificate that comes with your jewelry against the details of your diamond. For larger stones with a laser inscription on the girdle, the certificate's report number will match." },
      { q: 'What information is included in the IGI certificate?', a: 'The IGI certification report covers carat weight, cut, colour, clarity, polish, and symmetry grades, along with measurements, proportions, and fluorescence.' },
    ],
  },
  {
    group: 'Care and Repair',
    items: [
      { q: 'Is the jewelry moisture and heat resistant?', a: 'Yes, our gold vermeil jewelry is safe for everyday moisture exposure, including showers. We still recommend removing it during prolonged heat exposure or high-intensity activity.' },
      { q: 'Is the jewelry scratch-proof?', a: 'No metal is entirely scratch-proof, and 24K gold, being pure and naturally soft, can show minor surface marks with regular wear over time. This is a normal characteristic of pure gold, not a defect.' },
      { q: 'How can I maintain my jewelry to ensure it lasts longer?', a: 'Store each piece in a dry, airtight pouch away from direct sunlight and other jewelry, wipe gently with a soft cloth after each wear, and avoid contact with chlorine, perfume, and harsh chemicals.' },
      { q: 'Will the diamonds fade or lose their sparkle over time?', a: 'No. Lab-grown diamonds are real diamonds and retain their brilliance permanently, just like natural diamonds. They do not fade, change colour, or lose sparkle with age.' },
      { q: 'Do you provide repair or re-plating services?', a: 'Yes. House of Seya offers re-plating services at a minimal fee.' },
    ],
  },
  {
    group: 'Payment & Pricing',
    items: [
      { q: 'Does the product price include GST?', a: 'Yes, the displayed price is inclusive of applicable GST.' },
      { q: 'Are there any hidden charges?', a: 'No. The price you see is the final price you pay, inclusive of GST. Additional charges may apply only for remote-location shipping or optional customisation.' },
      { q: 'What payment methods do you accept?', a: 'We accept all major bank credit/debit cards, net banking and UPI.' },
      { q: 'Do you offer Cash on Delivery (COD)?', a: 'Yes, we offer Cash on Delivery services at no additional fee.' },
    ],
  },
  {
    group: 'Customization & Personalisation',
    items: [
      { q: 'Can I customize my jewelry?', a: 'Yes, House of Seya offers customisation in design, gold vermeil finish, diamond specifications, and sizing.' },
      { q: 'Can I engrave my jewelry?', a: 'Yes, you can personalise your jewelry with an engraving.' },
      { q: 'How do I start a customization request?', a: 'Contact us at hello@houseofseya.com, call +91 8722447768, or reach us on WhatsApp with your design concept — our team will guide you through the process.' },
      { q: 'What is the process for customising my jewelry?', a: 'Share your design concept, and we will share a rendering for your approval. Once approved, we craft your piece within approximately 3 weeks.' },
      { q: 'How long does customization take?', a: 'Custom pieces typically take about 3 weeks from design approval to delivery.' },
    ],
  },
  {
    group: 'Gifting',
    items: [
      { q: 'Can I purchase this as a gift?', a: 'Yes! Our jewelry makes a perfect gift for every occasion — engagements, weddings, anniversaries, birthdays, and celebrations.' },
      { q: 'Do you offer gift wrapping?', a: 'Yes, every order arrives in House of Seya’s signature packaging, ready for gifting.' },
      { q: 'Can I include a gift message?', a: 'Yes, you can add a personalised gift message during checkout, or request it through our WhatsApp support.' },
    ],
  },
  {
    group: 'Customer Support',
    items: [
      { q: 'How can I contact customer support?', a: "Email us at hello@houseofseya.com or call +91 8722447768. We're available Monday to Saturday, 10 AM to 7 PM (IST)." },
      { q: 'What if I have a complaint?', a: 'We take all concerns seriously. Please email us at hello@houseofseya.com or call +91 8722447768, and our team will address your issue promptly.' },
    ],
  },
];

const ProductRail = ({ title, products }) => {
  const ref = useRef(null);
  const scroll = (dir) => {
    if (ref.current) ref.current.scrollBy({ left: dir * 300, behavior: 'smooth' });
  };
  if (!products || !products.length) return null;
  return (
    <section className="max-w-[1500px] mx-auto px-6 lg:px-10 py-10">
      <div className="flex items-end justify-between mb-6">
        <h3 className="font-serif text-2xl lg:text-3xl text-[#1a1a1a]">{title}</h3>
        <div className="hidden lg:flex items-center gap-2">
          <button onClick={() => scroll(-1)} className="w-9 h-9 border border-[#d8ceb9] flex items-center justify-center hover:border-[#999959] hover:text-[#999959] transition-colors"><ChevronLeft size={15} strokeWidth={1.4} /></button>
          <button onClick={() => scroll(1)} className="w-9 h-9 border border-[#d8ceb9] flex items-center justify-center hover:border-[#999959] hover:text-[#999959] transition-colors"><ChevronRight size={15} strokeWidth={1.4} /></button>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4 sm:hidden">
        {products.slice(0, 4).map(p => <ProductCard key={p.id} product={p} />)}
      </div>
      <div ref={ref} className="hidden sm:flex gap-4 lg:gap-6 overflow-x-auto no-scrollbar pb-2 snap-x">
        {products.map(p => (
          <div key={p.id} className="flex-shrink-0 w-[280px] snap-start">
            <ProductCard product={p} />
          </div>
        ))}
      </div>
    </section>
  );
};

const Product = () => {
  const { id } = useParams();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [qty, setQty] = useState(1);
  const [metal, setMetal] = useState('Yellow Gold');
  const [mainImg, setMainImg] = useState('');

  // Fetch product from API by canonical Product.id
  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    setError(null);

    fetchProductById(id).then((data) => {
      if (!isMounted) return;
      if (data) {
        setProduct(data);

        // Build deterministic image gallery (YG primary, YG remaining, WG primary, WG remaining, RG primary, RG remaining)
        const galleryItems = buildDeterministicGallery(data);
        const defaultVar = data.variants?.find(v => v.isDefault) || data.variants?.find(v => v.metalFinish === 'Yellow Gold') || data.variants?.[0];
        const initialMetal = defaultVar?.metalFinish || 'Yellow Gold';
        setMetal(initialMetal);

        // Initial main image = first image of the shared gallery
        if (galleryItems[0]) {
          setMainImg(galleryItems[0].url);
        }

        // Fetch related products
        fetchProducts({ isPublished: 'true', limit: 8 }).then(relData => {
          if (isMounted) {
            setRelatedProducts(relData.filter(p => p.id !== data.id));
          }
        });
      } else {
        setError('Product not found');
      }
      setLoading(false);
    });

    return () => { isMounted = false; };
  }, [id]);

  // Deterministic 5-image gallery list across all variants (YG -> WG -> RG)
  const allGalleryItems = useMemo(() => {
    return buildDeterministicGallery(product);
  }, [product]);

  // Current active variant for pricing/cart
  const currentVariant = useMemo(() => {
    if (!product || !product.variants) return null;
    return product.variants.find(v => v.metalFinish.toLowerCase() === metal.toLowerCase()) || product.variants[0];
  }, [product, metal]);

  // Changing the metal finish only affects price/cart variant — the gallery image stays as is
  const handleMetalChange = (newMetal) => {
    setMetal(newMetal);
  };

  // Thumbnail click only changes the displayed image
  const handleThumbnailClick = (item) => {
    setMainImg(item.url);
  };

  const handleAdd = () => {
    if (!product) return;
    const cartProduct = {
      ...product,
      productId: product.id,
      variantId: currentVariant?.id,
      variantName: currentVariant?.metalFinish || metal,
      image: mainImg || (allGalleryItems[0]?.url || '')
    };
    for (let i = 0; i < qty; i++) {
      addItem(cartProduct);
    }
    toast.success('Added to cart', { description: `${product.name} (${metal})` });
  };

  const buildProductWhatsAppUrl = () => {
    if (!product) return '#';
    const WHATSAPP_NUMBER = '918722447768';
    const message = `Hi House of Seya! I'd like to buy the ${product.name} in ${metal} (${formatPrice(product.price)}). Is it available?`;
    return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;
  };

  if (loading) {
    return (
      <main className="bg-[#FFFFFF] min-h-screen py-20 flex items-center justify-center">
        <div className="text-center font-serif text-lg text-[#7a6a54]">Loading product details...</div>
      </main>
    );
  }

  if (error || !product) {
    return (
      <main className="bg-[#FFFFFF] min-h-screen py-20 flex flex-col items-center justify-center">
        <div className="font-serif text-2xl text-[#1a1a1a] mb-2">Product Not Found</div>
        <p className="text-sm text-[#7a6a54]">The product you are looking for does not exist or has been removed.</p>
      </main>
    );
  }

  return (
    <main className="bg-[#FFFFFF] min-h-screen">
      <div className="max-w-[1500px] mx-auto px-6 lg:px-10 py-8 grid lg:grid-cols-2 gap-8 lg:gap-16">
        {/* Gallery — main image + ONE UNIFIED THUMBNAIL STRIP (all 5 thumbnails always visible).
            Mobile: image on top, thumbnails below. Desktop (lg+): thumbnails in a column at the
            top-left, and the 3:4 main image is sized so it always fits inside the screen height. */}
        <div className="lg:flex lg:flex-row-reverse lg:justify-end lg:items-start lg:gap-4">
          <div className="w-full lg:w-[min(calc(100%-6rem),calc((100vh-9rem)*0.75))] lg:flex-shrink-0 aspect-[3/4] overflow-hidden bg-[#f7f4ee]">
            <img src={mainImg || (allGalleryItems[0]?.url || '')} alt={product.name} className="w-full h-full object-cover" />
          </div>
          {/* Always show all thumbnails together, without metal filtering */}
          <div className="flex gap-2 sm:gap-3 mt-3 overflow-x-auto no-scrollbar lg:mt-0 lg:flex-col lg:w-20 lg:flex-shrink-0 lg:overflow-x-hidden lg:overflow-y-auto lg:max-h-[calc(100vh-9rem)]">
            {allGalleryItems.map((item) => (
              <button
                key={item.id}
                onClick={() => handleThumbnailClick(item)}
                className={`flex-shrink-0 w-16 sm:w-20 aspect-square overflow-hidden bg-[#f7f4ee] transition-opacity ${mainImg === item.url ? 'opacity-100 ring-1 ring-[#999959]' : 'opacity-60 hover:opacity-100'}`}
              >
                <img src={item.url} alt={item.altText || ''} className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>

        {/* Product info */}
        <div className="lg:sticky lg:top-32 lg:self-start">
          <div className="text-[11px] tracking-widest-2 uppercase text-[#999959]">
            {product.inStock ? 'In Stock' : 'Made to Order'}
          </div>
          <h1 className="font-serif text-3xl lg:text-4xl text-[#1a1a1a] mt-2 leading-tight">{product.name}</h1>
          <div className="mt-3 flex items-baseline gap-3">
            {product.originalPrice && Number(product.originalPrice) > Number(product.price) ? (
              <>
                <div className="text-[14px] text-[#9a9386] line-through">{formatPrice(product.originalPrice)}</div>
                <div className="text-[15px] font-medium text-[#1a1a1a]">{formatPrice(product.price)}</div>
              </>
            ) : (
              <div className="text-[14px] font-medium text-[#1a1a1a]">{formatPrice(product.price)}</div>
            )}
            <div className="text-[12px] text-[#7a6a54]">Incl. of all taxes</div>
          </div>

          {/* Metal swatches — square */}
          <div className="mt-6">
            <div className="flex gap-5">
              {[
                { name: 'Yellow Gold', swatch: '#E6C158' },
                { name: 'White Gold', swatch: '#E4E1D8' },
                { name: 'Rose Gold', swatch: '#E6B7A0' },
              ].map(({ name, swatch }) => (
                <button
                  key={name}
                  onClick={() => handleMetalChange(name)}
                  aria-label={name}
                  aria-pressed={metal === name}
                  className="group flex flex-col items-center gap-2"
                >
                  <span className={`flex items-center justify-center p-[2px] border transition-colors ${metal === name ? 'border-[#1a1a1a]' : 'border-transparent group-hover:border-[#d8ceb9]'}`}>
                    <span
                      className="w-6 h-6 border border-black/10"
                      style={{ backgroundColor: swatch }}
                    />
                  </span>
                  <span className={`text-[10px] tracking-widest-2 uppercase transition-colors ${metal === name ? 'text-[#1a1a1a]' : 'text-[#a89a80] group-hover:text-[#7a6a54]'}`}>
                    {name}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Qty + Add to Cart */}
          <div className="mt-6 flex items-center gap-3">
            <div className="flex items-center border border-[#d8ceb9]">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="w-10 h-10 flex items-center justify-center hover:bg-[#f7f4ee] transition-colors"><Minus size={13} /></button>
              <span className="w-9 text-center text-[13px]">{qty}</span>
              <button onClick={() => setQty(qty + 1)} className="w-10 h-10 flex items-center justify-center hover:bg-[#f7f4ee] transition-colors"><Plus size={13} /></button>
            </div>
            <button
              onClick={handleAdd}
              className="flex-1 bg-[#999959] text-[#FFFFFF] py-3 text-[11px] tracking-widest-2 uppercase hover:bg-[#7d8449] transition-colors"
            >Add to Cart</button>
          </div>

          {/* Buy on WhatsApp */}
          <a
            href={buildProductWhatsAppUrl()}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-3 w-full flex items-center justify-center gap-2.5 border border-[#1a1a1a] text-[#1a1a1a] bg-white py-3 text-[11px] tracking-widest-2 uppercase hover:bg-[#1a1a1a] hover:text-white transition-colors"
          >
            <WhatsAppIcon size={18} color="#3fc352" />
            <span>Chat on WhatsApp</span>
          </a>

          {/* Promise items */}
          <ul className="mt-8 space-y-3">
            {PROMISE_ITEMS.map(({ icon: Icon, label }) => (
              <li key={label} className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-[#f7f4ee] flex items-center justify-center flex-shrink-0">
                  <Icon size={14} strokeWidth={1.4} className="text-[#c9a86b]" />
                </div>
                <span className="text-[13px] text-[#5a5346]">{label}</span>
              </li>
            ))}
          </ul>
          {!product.inStock && (
            <p className="mt-4 text-[12px] text-[#7a6a54] leading-relaxed border-t border-[#e5ddce] pt-4">
              This piece is made to order — please allow approximately 3 weeks for crafting and delivery.
            </p>
          )}

          {/* Details accordion */}
          <Accordion type="single" collapsible className="mt-8 w-full">
            {/* Description */}
            <AccordionItem value="description" className="border-[#d8ceb9]">
              <AccordionTrigger className="text-[12px] tracking-widest-2 uppercase text-[#1a1a1a] py-5 hover:no-underline">
                Description
              </AccordionTrigger>
              <AccordionContent className="text-[13px] text-[#5a5346] leading-relaxed pb-5">
                {product.description}
              </AccordionContent>
            </AccordionItem>

            {/* Product Details */}
            <AccordionItem value="details" className="border-[#d8ceb9]">
              <AccordionTrigger className="text-[12px] tracking-widest-2 uppercase text-[#1a1a1a] py-5 hover:no-underline">
                Product Details
              </AccordionTrigger>
              <AccordionContent className="text-[13px] text-[#5a5346] leading-relaxed pb-5">
                <p>24K Gold Vermeil over BIS hallmarked sterling silver</p>
                <div className="mt-4 space-y-1">
                  <div className="flex justify-between"><span>Product weight</span><span className="text-[#1a1a1a]">{product.netWeightGrams ? `${product.netWeightGrams} g` : '3.20 g'}</span></div>
                  <div className="flex justify-between"><span>Total diamond weight</span><span className="text-[#1a1a1a]">{product.totalDiamondCt ? `${product.totalDiamondCt} ct` : '0.85 ct'}</span></div>
                </div>
                <div className="mt-7 text-[12px] tracking-widest-2 uppercase text-[var(--brand-mallard-green)]">Diamond Details</div>
                <div className="mt-2 space-y-1">
                  <div className="flex justify-between"><span>Total diamond pieces</span><span className="text-[#1a1a1a]">{product.totalDiamondPcs ? product.totalDiamondPcs : '21'}</span></div>
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Materials & Details */}
            <AccordionItem value="materials" className="border-[#d8ceb9]">
              <AccordionTrigger className="text-[12px] tracking-widest-2 uppercase text-[#1a1a1a] py-5 hover:no-underline">
                Materials &amp; Details
              </AccordionTrigger>
              <AccordionContent className="text-[13px] text-[#5a5346] leading-relaxed pb-5">
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <span className="mt-[7px] w-1 h-1 rounded-full bg-[#5a5346] flex-shrink-0" />
                    <span>All our diamonds are IGI Certified EF colour and VVS-VS clarity</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="mt-[7px] w-1 h-1 rounded-full bg-[#5a5346] flex-shrink-0" />
                    <span>All our pieces are 24K gold coated with 925 sterling silver base</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="mt-[7px] w-1 h-1 rounded-full bg-[#5a5346] flex-shrink-0" />
                    <span>Customisation can be done on request</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="mt-[7px] w-1 h-1 rounded-full bg-[#5a5346] flex-shrink-0" />
                    <span>We have free engraving option on all products.</span>
                  </li>
                </ul>
              </AccordionContent>
            </AccordionItem>

            {/* Orders & Shipments */}
            <AccordionItem value="orders" className="border-[#d8ceb9]">
              <AccordionTrigger className="text-[12px] tracking-widest-2 uppercase text-[#1a1a1a] py-5 hover:no-underline">
                Orders &amp; Shipments
              </AccordionTrigger>
              <AccordionContent className="pb-5">
                <div className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide pl-4 pr-1 gap-8 lg:gap-10">
                  {ORDERS_SHIPMENTS_ITEMS.map((item, i) => (
                    <div key={i} className="flex-shrink-0 w-[85%] sm:w-[45%] lg:w-1/3 snap-start">
                      <item.icon size={22} strokeWidth={1.3} className="text-[#1a1a1a] mb-4" />
                      <div className="text-[15px] text-[#1a1a1a] leading-snug mb-2">{item.q}</div>
                      <ul className="space-y-1.5">
                        {item.bullets.map((segments, bi) => (
                          <li key={bi} className="flex items-start gap-2 text-[13px] text-[#5a5346] leading-relaxed">
                            <span className="mt-[7px] w-1 h-1 rounded-full bg-[#5a5346] flex-shrink-0" />
                            <span>
                              {segments.map((seg, si) =>
                                typeof seg === 'string' ? seg : <span key={si} className="font-medium text-[#1a1a1a]">{seg.b}</span>
                              )}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Returns & Exchanges */}
            <AccordionItem value="returns" className="border-[#d8ceb9]">
              <AccordionTrigger className="text-[12px] tracking-widest-2 uppercase text-[#1a1a1a] py-5 hover:no-underline">
                Returns &amp; Exchanges
              </AccordionTrigger>
              <AccordionContent className="pb-5">
                <div className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide pl-4 pr-1 gap-8 lg:gap-10">
                  {RETURNS_POLICY_CARDS.map((card, i) => (
                    <div key={i} className="flex-shrink-0 w-[85%] sm:w-[45%] lg:w-1/3 snap-start">
                      <div className="text-[15px] text-[#1a1a1a] leading-snug mb-2">{card.title}</div>
                      {card.bullets && (
                        <ul className="space-y-1.5">
                          {card.bullets.map((b, j) => (
                            <li key={j} className="flex items-start gap-2 text-[13px] text-[#5a5346] leading-relaxed">
                              <span className="mt-[7px] w-1 h-1 rounded-full bg-[#5a5346] flex-shrink-0" />
                              {b}
                            </li>
                          ))}
                        </ul>
                      )}
                      {card.body && (
                        <p className={`text-[13px] text-[#5a5346] leading-relaxed ${card.bullets ? 'mt-2' : ''}`}>{card.body}</p>
                      )}
                    </div>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* FAQ's */}
            <AccordionItem value="faqs" className="border-[#d8ceb9] data-[state=open]:border-b-0">
              <AccordionTrigger className="text-[12px] tracking-widest-2 uppercase text-[#1a1a1a] py-5 hover:no-underline">
                FAQ's
              </AccordionTrigger>
              <AccordionContent className="pb-2">
                <Accordion type="single" collapsible className="w-full">
                  {PRODUCT_FAQ_GROUPS.map((group, gi) => (
                    <AccordionItem key={gi} value={`faq-group-${gi}`} className="border-[#e5ddce]">
                      <AccordionTrigger className="text-[11px] tracking-widest-2 uppercase text-[#7a6a54] py-3 hover:no-underline">
                        {group.group}
                      </AccordionTrigger>
                      <AccordionContent className="pb-4">
                        <div className="space-y-4 pl-4 border-l border-[#e5ddce]">
                          {group.items.map((faq, i) => (
                            <div key={i}>
                              <div className="text-[13px] font-medium text-[#1a1a1a] leading-snug mb-1">{faq.q}</div>
                              <div className="text-[12px] text-[#5a5346] leading-relaxed">{faq.a}</div>
                            </div>
                          ))}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </div>

      <ProductRail title="Related Products" products={relatedProducts} />
    </main>
  );
};

// Gallery builder: one shared set of product images (independent of metal finish).
// Legacy products that only have per-finish images fall back to those (default finish first).
function buildDeterministicGallery(product) {
  if (!product) return [];

  const toItem = (img, fallbackAlt) => ({
    id: img.id,
    url: resolveImageUrl(img.url),
    isPrimary: Boolean(img.isPrimary),
    altText: img.altText || fallbackAlt,
  });

  if (product.images && product.images.length > 0) {
    return [...product.images]
      .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
      .map((img) => toItem(img, product.name));
  }

  const items = [];
  const seen = new Set();
  const sortedVariants = [...(product.variants || [])].sort((a, b) => Number(b.isDefault) - Number(a.isDefault));
  for (const v of sortedVariants) {
    const sortedImages = [...(v.images || [])].sort((a, b) => {
      if (a.isPrimary && !b.isPrimary) return -1;
      if (!a.isPrimary && b.isPrimary) return 1;
      return (a.sortOrder || 0) - (b.sortOrder || 0);
    });
    for (const img of sortedImages) {
      if (seen.has(img.url)) continue;
      seen.add(img.url);
      items.push(toItem(img, product.name));
    }
  }
  return items;
}

export default Product;
